import { useState, useEffect, useCallback } from 'react';
import {
  GitBranch,
  Lock,
  Star,
  Trophy,
  Play,
  RefreshCw,
  Check,
  Loader2
} from 'lucide-react';

import { Card, CardContent } from '../ui/card';
import { BeltIndicator } from '../shared/BeltBadge';

import ScenarioPlayer from './ScenarioPlayer';


const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Scenario categories
const SCENARIO_CATEGORIES = [
  { value: '', label: 'All', icon: null },
  { value: 'self_defense', label: 'Self-Defense', icon: null },
  { value: 'etiquette', label: 'Etiquette', icon: null },
  { value: 'tournament', label: 'Tournament', icon: null },
  { value: 'technique', label: 'Technique', icon: null },
  { value: 'conflict_resolution', label: 'Conflict', icon: null },
];

// Outcome colors
const OUTCOME_COLORS = {
  optimal: 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/50',
  good: 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/50',
  neutral: 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700',
  poor: 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/50',
  failure: 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/50',
};

// ============================================
// Progress Summary Card
// ============================================
const ProgressSummary = ({ progress }) => {
  if (!progress) return null;

  return (
    <Card className="mb-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Trophy className="h-8 w-8 text-purple-500" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Your Progress</p>
              <p className="font-semibold text-gray-900 dark:text-gray-100">
                {progress.completed_scenarios} / {progress.accessible_scenarios} completed
              </p>
            </div>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{progress.optimal_endings}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Optimal</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{progress.total_points_earned}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Points</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================
// Scenario Card
// ============================================
const ScenarioCard = ({ scenario, onPlay }) => {
  const isLocked = !scenario.has_access;
  const isCompleted = scenario.is_completed;
  const hasOptimal = scenario.achieved_optimal;

  return (
    <Card
      className={`relative overflow-hidden transition-all ${
        isLocked ? 'opacity-60' : 'hover:shadow-md cursor-pointer'
      }`}
      onClick={() => !isLocked && onPlay(scenario)}
    >
      {/* Featured badge */}
      {scenario.is_featured && !isLocked && (
        <div className="absolute top-2 right-2">
          <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
        </div>
      )}

      {/* Completion badge */}
      {isCompleted && (
        <div className="absolute top-2 left-2">
          <div className={`rounded-full p-1 ${hasOptimal ? 'bg-green-500' : 'bg-blue-500'}`}>
            {hasOptimal ? (
              <Trophy className="h-4 w-4 text-white" />
            ) : (
              <Check className="h-4 w-4 text-white" />
            )}
          </div>
        </div>
      )}

      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <span className="text-3xl">{scenario.icon_emoji}</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{scenario.title}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 capitalize mb-2">
              {scenario.category?.replace('_', ' ')}
            </p>

            {scenario.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
                {scenario.description}
              </p>
            )}

            {/* Difficulty & Points */}
            <div className="flex items-center gap-3 text-xs">
              <span className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded">
                Level {scenario.difficulty}
              </span>
              <span className="bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 px-2 py-1 rounded">
                +{scenario.base_points} pts
              </span>
            </div>

            {/* Best ending (if completed) */}
            {isCompleted && scenario.best_ending && (
              <div className="mt-2">
                <span className={`text-xs px-2 py-1 rounded-full ${OUTCOME_COLORS[scenario.best_ending] || OUTCOME_COLORS.neutral}`}>
                  Best: {scenario.best_ending}
                </span>
              </div>
            )}
          </div>

          {/* Action indicator */}
          <div className="flex-shrink-0">
            {isLocked ? (
              <div className="flex flex-col items-center text-gray-400">
                <Lock className="h-6 w-6" />
                {scenario.required_belt && (
                  <BeltIndicator beltSlug={scenario.required_belt} size="sm" className="mt-1" />
                )}
              </div>
            ) : isCompleted ? (
              <RefreshCw className="h-6 w-6 text-blue-500" />
            ) : (
              <Play className="h-6 w-6 text-green-500" />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================
// Main ScenarioSection Component
// ============================================
const ScenarioSection = ({ student, getAuthHeaders }) => {
  const [scenarios, setScenarios] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [playingScenario, setPlayingScenario] = useState(null);

  const loadScenarios = useCallback(async () => {
    if (!student?.id) return;

    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);

      const response = await fetch(
        `${API_BASE}/api/customer/students/${student.id}/scenarios?${params}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setScenarios(data);
      }
    } catch (error) {
      console.error('Failed to load scenarios:', error);
    }
  }, [student?.id, selectedCategory, getAuthHeaders]);

  const loadProgress = useCallback(async () => {
    if (!student?.id) return;

    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${student.id}/scenarios/progress`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setProgress(data);
      }
    } catch (error) {
      console.error('Failed to load progress:', error);
    }
  }, [student?.id, getAuthHeaders]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([loadScenarios(), loadProgress()]);
      setLoading(false);
    };
    load();
  }, [loadScenarios, loadProgress]);

  const handlePlayScenario = (scenario) => {
    setPlayingScenario(scenario);
  };

  const handleExitScenario = (shouldRefresh = false) => {
    setPlayingScenario(null);
    if (shouldRefresh) {
      loadScenarios();
      loadProgress();
    }
  };

  // If playing a scenario, show the player
  if (playingScenario) {
    return (
      <ScenarioPlayer
        scenario={playingScenario}
        student={student}
        getAuthHeaders={getAuthHeaders}
        onExit={handleExitScenario}
      />
    );
  }

  // Separate accessible and locked scenarios
  const accessibleScenarios = scenarios.filter(s => s.has_access);
  const lockedScenarios = scenarios.filter(s => !s.has_access);

  return (
    <div className="space-y-4">
      {/* Progress Summary */}
      <ProgressSummary progress={progress} />

      {/* Category Filter */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {SCENARIO_CATEGORIES.map(cat => (
          <button
            key={cat.value}
            onClick={() => setSelectedCategory(cat.value)}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
              selectedCategory === cat.value
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        </div>
      ) : scenarios.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <GitBranch className="h-12 w-12 mx-auto mb-4 text-gray-400 dark:text-gray-500 dark:text-gray-400" />
            <p className="text-gray-500 dark:text-gray-400">No scenarios available yet</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Check back soon!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Accessible Scenarios */}
          {accessibleScenarios.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <GitBranch className="h-4 w-4" />
                Available Scenarios ({accessibleScenarios.length})
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                {accessibleScenarios.map(scenario => (
                  <ScenarioCard
                    key={scenario.id}
                    scenario={scenario}
                    onPlay={handlePlayScenario}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Locked Scenarios */}
          {lockedScenarios.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Locked ({lockedScenarios.length})
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                {lockedScenarios.map(scenario => (
                  <ScenarioCard
                    key={scenario.id}
                    scenario={scenario}
                    onPlay={handlePlayScenario}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScenarioSection;
